import { debouncer, type XSSDetails, generateMagicInterval, generateMagicIntervals as _generateMagicIntervals, getRecommendChallengeLevel } from '@/Util/utils';
import { _ } from 'svelte-i18n';
import { type MagicBucketsData, type Signature } from '@/Common/constants';

type MagicBucketsType = 'workout' | 'autogen' | 'manual';
export const magicBucketTypeInfo: Record<MagicBucketsType, { label: string; faIcon: string; description: string }> = {
  workout: { label: 'Workout', faIcon: 'fa-dumbbell', description: 'This creates a workout using the Magic Buckets algorithm based Challenge Level. Choose the Challenge Level you prefer based on Workout Duration and/or Difficulty.' },
  manual: { label: 'Manual', faIcon: 'fa-pen', description: 'This allows you to schedule a manual activity to the planner that you intend to do using Magic Buckets without requiring a pre-defined workout.' },
  autogen: { label: 'Autogen', faIcon: 'fa-sparkles', description: "This generates a workout using the Xert's AI Workout Generator tool which features a reduced number of intervals that are tapered as the workout progresses." },
};

export class MagicBucketsFieldsAndData {
  reactivityCallback: () => void;

  constructor(reactivityCallback: () => void) {
    this.reactivityCallback = reactivityCallback;
  }

  targets: XSSDetails<number>;
  recommendedWorkoutDuration: number;
  signature: Signature;

  mpaData: any = null;
  selectedChallengeLevel: number | null = null;

  magicIntervalData = null;
  magicIntervalChartData: Promise<MagicBucketsData> = null;
  intervalDurationMultiplier = 1.5;
  rampupMultiplier = 1.0;
  magicDebouncer = debouncer(500);
  intervalDuration = 0;
  sliderIntervalDuration = this.intervalDuration;
  baseIntervalDuration = 0;

  selectMagicInterval = (challengeLevel: number) => {
    this.selectedChallengeLevel = challengeLevel;
    this.magicIntervalChartData = new Promise((resolve) => setTimeout(resolve, 500)).then(async () => {
      this.mpaData = await generateMagicInterval(this.signature, this.targets, this.intervalDurationMultiplier, this.rampupMultiplier, this.selectedChallengeLevel);
      return this.mpaData;
    });
    this.reactivityCallback();
  }

  setIntervalDuration = (duration: number) => {
    this.intervalDuration = duration;
    this.sliderIntervalDuration = duration;
    this.intervalDurationMultiplier = this.intervalDuration / this.baseIntervalDuration;
    this.rampupMultiplier = this.intervalDurationMultiplier / 1.5;
    this.reactivityCallback();
  }

  generateMagicIntervals = async () => {
    this.magicDebouncer.debounce(async () => {
      if (this.baseIntervalDuration == 0) {
        const data = await generateMagicInterval(this.signature, this.targets, this.intervalDurationMultiplier, this.rampupMultiplier, 3.0, false);
        this.baseIntervalDuration = data.summary.baseIntervalDuration;
      }
      if (this.intervalDuration == 0) {
        this.setIntervalDuration(this.baseIntervalDuration * this.intervalDurationMultiplier);
      }
      this.reactivityCallback();
      const data = await _generateMagicIntervals(this.signature, this.targets, this.intervalDurationMultiplier, this.rampupMultiplier);

      if (data) {
        this.magicIntervalData = data;
        this.selectedChallengeLevel = this.selectedChallengeLevel ?? getRecommendChallengeLevel(this.recommendedWorkoutDuration, data);
        this.selectMagicInterval(this.selectedChallengeLevel);
      }
      this.reactivityCallback();
    });
  }

  initMagicBuckets = () => {
    this.magicIntervalData = null;
    this.magicIntervalChartData = null;
    this.selectedChallengeLevel = null;
    this.intervalDuration = 0;
    this.baseIntervalDuration = 0;
    this.intervalDurationMultiplier = 1.5;
    this.rampupMultiplier = 1.0;
    this.signature = null;
    this.targets = null;
    this.reactivityCallback();
  }

  updateTargets = (targets: XSSDetails<number>) => {
    if (targets.xpss == 0 && targets.xhss == 0) {
      this.magicIntervalData = null;
      this.magicIntervalChartData = null;
      this.selectedChallengeLevel = null;
    }
    this.targets = targets;
    if (targets.xss > 0 && (targets.xhss > 0 || targets.xpss > 0)) this.generateMagicIntervals();
    else this.reactivityCallback();
  }
}